// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, Category, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateCategoryStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateCategoryStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateCategoryStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogCategory = searchParams.get('dialogCategory');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [category, setCategory] = useState<Category | null>(null);

	// fetch category
	const { isLoading: isLoadingCategory } = useFetch({
		isEnabled: Boolean(paramDialogCategory && isOpen),
		url: `${process.env.REACT_APP_API_URL}/categories/${paramDialogCategory}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategory(res.data[0]);
			}
		},
	});

	// vars
	const dialogActionProps: Partial<DialogActionsProps> = {};
	const isActivating = paramDialogStatus === 'ACTIVE';

	// deactivate translations
	const dialog = {
		alert: t('alert.deactivateCategory'),
		btnSubmit: {
			children: t('deactivateCategory'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateCategoryStatus.deactivate.content',
		title: t('deactivateCategory'),
	};

	// activate translations
	if (isActivating) {
		dialog.btnSubmit = {
			children: t('activateCategory'),
			color: 'primary',
		};
		dialog.alert = t('alert.activateCategory');
		dialog.i18nKey = 'dialogUpdateCategoryStatus.activate.content';
		dialog.title = t('activateCategory');
	}

	if (category) {
		dialogActionProps.subtitle = {
			children: category.name,
		};
	}

	// patch category
	const { fetchRequest: updateCategory, isLoading: isLoadingUpdateCategory } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/categories/${paramDialogCategory}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				status: paramDialogStatus,
			}),
		},
		onError: (feedback) => {
			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (feedback.id === 'CATEGORY_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('feedback.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			let message = t('alert.categoryDeactivated', { name: category?.name });

			if (isActivating) {
				message = t('alert.categoryActivated', { name: category?.name });
			}

			// set banner
			setBanner({
				hasClose: true,
				message,
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// update category
		updateCategory();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingCategory) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: category?.name }}
					/>
				</Grid>
				{dialog.alert && (
					<Grid item xs={12}>
						<Alert severity="warning">{dialog.alert}</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateCategory,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'category',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: dialog.title,
			}}
			{...dialogActionProps}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
